// "Konto & Zugang"-Gruppe der Settings-Navigation: Postfach-Verbindung,
// API-Tokens (MCP & REST), Bots. Ausgelagert aus routes/Settings.tsx
// (Redesign: 15 Abschnitte in Tabs gruppiert) nur um die Hauptdatei unter
// der 500-Zeilen-Grenze zu halten — wortgleiche Verschiebung, Logik bleibt
// in Settings.tsx.

import { Mail } from "lucide-preact";
import type { MailConnectionDto } from "@juuplan/shared";
import { Button } from "../../components/ui/Button";
import { ApiTokensSection } from "../../components/ApiTokensSection";
import { BotsSection } from "../../components/BotsSection";

export interface AccountGroupProps {
  mailConn: MailConnectionDto | null;
  onDisconnectMailbox: () => void;
}

export function AccountGroup(props: AccountGroupProps): preact.JSX.Element {
  const { mailConn } = props;
  return (
    <div class="flex flex-col gap-8">
      {/* Delegated mailbox — opt-in "send as me" for auto follow-ups. Only
          shown once the admin has enabled the send-on-behalf feature. */}
      {mailConn?.feature_enabled && (
        <section>
          <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
            Postfach-Verbindung
          </h2>
          <div class="flex items-center justify-between gap-4 rounded-lg border border-[color:var(--color-outline-variant)] p-4">
            <div class="flex items-start gap-3">
              <Mail size={20} class="mt-0.5 text-[color:var(--color-primary)]" aria-hidden="true" />
              <div>
                <p class="text-sm font-medium text-[color:var(--color-on-surface)]">
                  {mailConn.status === "connected"
                    ? "Postfach verbunden"
                    : mailConn.status === "needs_reconnect"
                      ? "Verbindung abgelaufen"
                      : "Postfach nicht verbunden"}
                </p>
                <p class="text-xs text-[color:var(--color-on-surface-variant)] max-w-md">
                  Verbinde dein Postfach, damit automatische Nachfragen an Kollegen aus
                  deinem eigenen Postfach versendet werden.
                </p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              {mailConn.status === "connected" ? (
                <Button variant="secondary" onClick={props.onDisconnectMailbox}>
                  Trennen
                </Button>
              ) : (
                <a
                  href="/api/auth/connect-mailbox"
                  class="rounded-md bg-[color:var(--color-primary)] px-3 py-2 text-sm font-medium text-[color:var(--color-on-primary)]"
                >
                  {mailConn.status === "needs_reconnect" ? "Erneut verbinden" : "Verbinden"}
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* API tokens — MCP fuer KI-Clients UND REST fuer Fremdanwendungen.
          Die Kopfzeile hiess frueher nur "(MCP)"; seit #957 holt sich auch
          Polaris hier sein Token, und wer eine Anleitung "Token in juuplan
          erstellen" liest, muss diesen Abschnitt wiedererkennen. */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          API-Tokens (MCP &amp; REST)
        </h2>
        <p class="text-xs text-[color:var(--color-on-surface-variant)] mb-3">
          Persönliche Zugriffs-Tokens — für KI-Clients wie Claude Code (als
          MCP-Server) und für andere Anwendungen, die juuplan über die
          REST-API lesen, etwa Polaris. Tokens haben genau deine Rechte,
          „Nur lesen" reicht zum Anzeigen, und sie lassen sich jederzeit
          widerrufen.
        </p>
        <ApiTokensSection />
      </section>

      {/* Bots — eigene Accounts, die per MCP-Token im Auftrag des Users arbeiten. */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Bots
        </h2>
        <p class="text-xs text-[color:var(--color-on-surface-variant)] mb-3">
          Eigene Bot-Accounts, die per MCP-Token in deinem Auftrag
          mitarbeiten. Sie erben deine Rechte, sind per @Name ansprechbar
          und überall als Bot gekennzeichnet.
        </p>
        <BotsSection />
      </section>
    </div>
  );
}
